import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { buildNodeSettingsLink } from "../utils/nodeSettingsLink";

// Backend decides the status string - never recompute it here.
const AMBER = "#f59e0b";
const STATUS_COLORS = {
  "Within Range": "#16a34a",
  Amber: AMBER,
  "Out of Range": "#dc2626",
};
const STATUS_ICONS = {
  "Within Range": "✔",
  Amber: "⚠",
  "Out of Range": "✕",
};
// Retired label from the pre-amber heuristic; mapped so an older API build
// still renders in colour rather than falling through to grey.
const LEGACY_STATUS_ALIASES = { Caution: "Amber" };
const resolveStatus = (status) => LEGACY_STATUS_ALIASES[status] || status;
// "Amber" names the colour, not the condition, so the table says what the
// reading is doing instead. The API token stays "Amber".
const STATUS_LABELS = { Amber: "Near Limit" };
const statusLabel = (status) => STATUS_LABELS[status] || status;

const STANDARD_COLOR = "#22a06b";

// Table styling mirrors the Live table (channelStyles in LiveTab.js).
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle = {
  padding: "12px 8px",
  textAlign: "center",
  borderBottom: "2px solid #dee2e6",
  backgroundColor: "#f8f9fa",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
const tdStyle = {
  padding: "10px 8px",
  textAlign: "center",
  borderBottom: "1px solid #dee2e6",
};

const fmt = (v, digits = 1) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "-"
    : Number(v).toFixed(digits);

// Deviation is signed: show it with an explicit +/- like the QCP sheet does.
const fmtDeviation = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "-";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}`;
};

const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};

// QCP numbers are free text ("1", "10", "QCP 2"), so compare numerically-aware;
// plain string order would put 10 and 11 ahead of 2.
const compareText = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });
const compareNumber = (a, b) => Number(a) - Number(b);

const TEXT_COLUMNS = ["qcp_no", "location", "status"];
const isMissing = (key, v) =>
  TEXT_COLUMNS.includes(key) ? !v : v === null || v === undefined || Number.isNaN(Number(v));

const COLUMNS = [
  ["qcp_no", "QCP No", {}],
  ["location", "Location", { textAlign: "left" }],
  ["standard_temp", "Standard (°C)", {}],
  ["actual_temp", "Actual Temp (°C)", {}],
  ["lcl_temp", "LCL", {}],
  ["ucl_temp", "UCL", {}],
  ["deviation", "Deviation (°C)", {}],
  ["status", "Status", {}],
];

const filterCellStyle = { ...thStyle, padding: "6px 8px", borderBottom: "1px solid #dee2e6" };
const filterInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "5px 8px",
  fontSize: 12,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  background: "#fff",
  fontWeight: 400,
};

const SettingsGear = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="Edit QCP limits in Node Settings"
    aria-label="Edit QCP limits in Node Settings"
    style={{
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: 4,
      color: "#64748b",
      display: "inline-flex",
      alignItems: "center",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = "#1BA08E"; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = "#64748b"; }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  </button>
);

const LegendSwatch =({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span
      style={{
        width: 11,
        height: 11,
        borderRadius: 3,
        background: color,
        display: "inline-block",
      }}
    />
    {label}
  </span>
);

const QcpSummary = ({ username, startDate, endDate }) => {
  const navigate = useNavigate();
  const [allRows, setRows] = useState([]);
  // Table opens sorted by QCP number; headers re-sort, the row beneath filters.
  const [sort, setSort] = useState({ key: "qcp_no", dir: "asc" });
  const [filters, setFilters] = useState({ qcp_no: "", location: "", status: "" });
  const [period, setPeriod] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    try {
      // start_date / end_date match the other Historical endpoints. actual_temp,
      // deviation and status are all averaged over this range by the backend.
      const params = { username };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get("/qcp-summary", { params });
      if (res.data && res.data.status === "success") {
        setRows(res.data.data || []);
        setPeriod({
          from: res.data.from || res.data.date || startDate || "",
          to: res.data.to || res.data.date || endDate || "",
        });
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("Fetch qcp-summary error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [username, startDate, endDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const rows = useMemo(() => {
    const qcp = filters.qcp_no.trim().toLowerCase();
    const loc = filters.location.trim().toLowerCase();
    const filtered = allRows.filter(
      (r) =>
        (!qcp || String(r.qcp_no ?? "").toLowerCase().includes(qcp)) &&
        (!loc || String(r.location ?? "").toLowerCase().includes(loc)) &&
        (!filters.status || resolveStatus(r.status) === filters.status),
    );
    const compare = TEXT_COLUMNS.includes(sort.key) ? compareText : compareNumber;
    const factor = sort.dir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => {
      // Rows with no value for the sorted column always go last.
      const aMissing = isMissing(sort.key, a[sort.key]);
      const bMissing = isMissing(sort.key, b[sort.key]);
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      return factor * compare(a[sort.key], b[sort.key]);
    });
  }, [allRows, filters, sort]);

  const toggleSort = (key) =>
    setSort((cur) =>
      cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  const setFilter = (key, value) => setFilters((cur) => ({ ...cur, [key]: value }));

  const chart = useMemo(() => {
    const categories = rows.map((r) => r.location || r.qcp_no || "");
    return {
      series: [
        {
          name: "Standard (°C)",
          data: rows.map((r) => ({
            x: r.location || r.qcp_no,
            y: r.standard_temp,
            fillColor: STANDARD_COLOR,
          })),
        },
        {
          name: "Actual Temp (°C)",
          // Each Actual bar is tinted by the status the backend returned.
          data: rows.map((r) => ({
            x: r.location || r.qcp_no,
            y: r.actual_temp,
            fillColor: STATUS_COLORS[resolveStatus(r.status)] || "#94a3b8",
          })),
        },
      ],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "inherit",
          animations: { enabled: false },
        },
        plotOptions: {
          bar: { columnWidth: "60%", dataLabels: { position: "top" } },
        },
        dataLabels: {
          enabled: true,
          offsetY: -18,
          style: { fontSize: "11px", fontWeight: 700, colors: ["#334155"] },
          formatter: (val) => (val === null ? "" : Number(val).toFixed(1)),
        },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories,
          title: { text: "Quality Control Point", style: { fontSize: "12px" } },
          labels: {
            rotate: -45,
            rotateAlways: true,
            style: { fontSize: "8px" },
          },
        },
        yaxis: {
          title: { text: "Temperature (°C)", style: { fontSize: "12px" } },
          labels: { formatter: (v) => Number(v).toFixed(0) },
        },
        legend: { show: false },
        grid: { borderColor: "#e2e8f0" },
        tooltip: { y: { formatter: (v) => (v === null ? "-" : `${Number(v).toFixed(1)} °C`) } },
      },
    };
  }, [rows]);

  // Empty data -> hide the whole section (sensors without limits configured).
  if (loading && allRows.length === 0) return null;
  if (!allRows.length) return null;

  return (
    <div>
      <div
        style={{
          background: "#1BA08E",
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          padding: "8px 12px",
          borderRadius: "8px 8px 0 0",
        }}
      >
        Baseline QCP Data
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #dee2e6", borderTop: "none" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {COLUMNS.map(([key, label, extra]) => (
                <th
                  key={key}
                  style={{ ...thStyle, ...extra, cursor: "pointer", userSelect: "none" }}
                  onClick={() => toggleSort(key)}
                  aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  {label}
                  <span style={{ marginLeft: 4, fontSize: 10, color: sort.key === key ? "#1BA08E" : "#cbd5e1" }}>
                    {sort.key === key && sort.dir === "desc" ? "▼" : "▲"}
                  </span>
                </th>
              ))}
              <th style={{ ...thStyle, width: 44 }} aria-label="Settings" />
            </tr>
            <tr>
              <th style={filterCellStyle}>
                <input
                  type="text"
                  value={filters.qcp_no}
                  onChange={(e) => setFilter("qcp_no", e.target.value)}
                  placeholder="Filter"
                  aria-label="Filter by QCP No"
                  style={filterInputStyle}
                />
              </th>
              <th style={filterCellStyle}>
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => setFilter("location", e.target.value)}
                  placeholder="Filter location"
                  aria-label="Filter by location"
                  style={filterInputStyle}
                />
              </th>
              <th style={filterCellStyle} colSpan={5} />
              <th style={filterCellStyle}>
                <select
                  value={filters.status}
                  onChange={(e) => setFilter("status", e.target.value)}
                  aria-label="Filter by status"
                  style={filterInputStyle}
                >
                  <option value="">All</option>
                  {Object.keys(STATUS_COLORS).map((k) => (
                    <option key={k} value={k}>{statusLabel(k)}</option>
                  ))}
                </select>
              </th>
              <th style={filterCellStyle} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, color: "#64748b" }} colSpan={9}>
                  No QCP rows match the current filters.
                </td>
              </tr>
            )}
            {rows.map((r, idx) => {
              const status = resolveStatus(r.status);
              const statusColor = STATUS_COLORS[status] || "#64748b";
              return (
                <tr
                  key={r.meter_id || `${r.qcp_no}-${idx}`}
                  style={{ backgroundColor: idx % 2 === 1 ? "#f8f9fa" : "#fff" }}
                >
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{r.qcp_no || "-"}</td>
                  <td style={{ ...tdStyle, textAlign: "left" }}>{r.location || "-"}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(r.standard_temp)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: statusColor }}>
                    {fmt(r.actual_temp)}
                  </td>
                  <td style={{ ...tdStyle, color: "#dc2626" }}>{fmt(r.lcl_temp)}</td>
                  <td style={{ ...tdStyle, color: "#16a34a" }}>{fmt(r.ucl_temp)}</td>
                  <td style={tdStyle}>{fmtDeviation(r.deviation)}</td>
                  <td style={{ ...tdStyle, color: statusColor, fontWeight: 700, whiteSpace: "nowrap" }}>
                    <span style={{ marginRight: 6 }}>{STATUS_ICONS[status] || ""}</span>
                    {statusLabel(status) || "-"}
                  </td>
                  <td style={tdStyle}>
                    <SettingsGear
                      onClick={() =>
                        navigate(buildNodeSettingsLink({ meterId: r.meter_id, machineName: r.location }))
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          border: "1px solid #dee2e6",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          padding: "16px 8px 8px",
          background: "#fff",
        }}
      >
        <div
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 15,
            color: "#0f172a",
            marginBottom: 8,
          }}
        >
          QCP Temperatures: Standard vs Actual (
          {period.from && period.to && period.from !== period.to
            ? `${prettyDate(period.from)} to ${prettyDate(period.to)}`
            : prettyDate(period.from || period.to)}
          )
        </div>
        <ReactApexChart
          options={chart.options}
          series={chart.series}
          type="bar"
          height={420}
        />

        {/* Actual bars are coloured per status, so the legend explains the statuses
            rather than pretending the series has one colour. */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 18,
            paddingTop: 4,
            fontSize: 12,
            color: "#475569",
          }}
        >
          <LegendSwatch color={STANDARD_COLOR} label="Standard (°C)" />
          <span style={{ color: "#94a3b8" }}>|</span>
          <span style={{ fontWeight: 600 }}>Actual Temp (°C):</span>
          {Object.keys(STATUS_COLORS).map((k) => (
            <LegendSwatch key={k} color={STATUS_COLORS[k]} label={statusLabel(k)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default QcpSummary;
